import React from 'react';
import moment from 'moment-timezone';


/**
 * FormattedDateTime displays a date in a specific format, with optional timezone and locale.
 * 
 * USE DATEFORMAT OR CUSTOMIZE IT WITH DATE AND TIME
 *
 * @param {string|Date} dateValue - The date/time value to display.
 * @param {string} [dateFormat] - Optional full Moment.js format string, e.g., "DD/MM/YYYY HH:mm".
 * @param {Object} [date] - Optional object defining day, month, year, weekday formatting.
 * @param {("numeric"|"2-digit"|"ordinal")} [date.day] - How to display the day part.
 * @param {("numeric"|"2-digit"|"short"|"long")} [date.month] - How to display the month part.
 * @param {("numeric"|"2-digit")} [date.year] - How to display the year part.
 * @param {("short"|"long")} [date.weekday] - Display weekday at the beginning.
 * @param {string} [date.dateSeparator="/"] - Character to separate day/month/year (default "/").
 * @param {Object} [time] - Optional object defining hour, minute, second formatting.
 * @param {("numeric"|"2-digit")} [time.hour] - Hour format.
 * @param {("numeric"|"2-digit")} [time.minute] - Minute format.
 * @param {("numeric"|"2-digit")} [time.second] - Second format.
 * @param {string} [time.timeSeparator=":"] - Character to separate hour/minute/second (default ":").
 * @param {boolean} [time.ampm=false] - Whether to display in AM/PM format.
 * @param {string} [timeZone] - Optional timezone string (e.g., "America/Montevideo").
 * @param {string} [locale="en"] - Language/locale for month and weekday (supports "en" and "es").
 * @param {string} [ifEmpty=""] - Text to display if `dateValue` is empty or undefined.
 */

const DEFAULT_DATE_SEPARATOR = '/';
const DEFAULT_TIME_SEPARATOR = ':';
const DEFAULT_DATETIME_FORMAT = 'DD/MM/YYYY HH:mm';
const DEFAULT_EMPTY_DATE = '';

export default function FormattedDateTime({
	dateValue,
	dateFormat,
	date,
	time,
	timeZone,
	locale = 'es', // 'en' or 'es'
	ifEmpty = ''
}) {
	if (!dateValue) return <>{ifEmpty || DEFAULT_EMPTY_DATE}</>;

	let formatString = dateFormat || DEFAULT_DATETIME_FORMAT;
	const dateSep = date?.dateSeparator || DEFAULT_DATE_SEPARATOR;
	const timeSep = time?.timeSeparator || DEFAULT_TIME_SEPARATOR;

	if (!dateFormat && date && time) {
		const dynamicFormat = {
			day: date.day === '2-digit' ? 'DD' : date.day === 'numeric' ? 'D' : date.day === 'ordinal' ? 'Do' : 'DD',
			month:
				date.month === '2-digit'
					? 'MM'
					: date.month === 'numeric'
						? 'M'
						: date.month === 'short'
							? 'MMM'
							: date.month === 'long'
								? 'MMMM'
								: 'MM',
			year: date.year === '2-digit' ? 'YY' : 'YYYY',
			weekday:
				date.weekday === 'short'
					? 'ddd, '
					: date.weekday === 'long'
						? 'dddd, '
						: '',
			hour:
				time.hour === '2-digit'
					? (time.ampm ? 'hh' : 'HH')
					: time.hour === 'numeric'
						? (time.ampm ? 'h' : 'H')
						: (time.ampm ? 'hh' : 'HH'),
			minute:
				time.minute === '2-digit'
					? 'mm'
					: time.minute === 'numeric'
						? 'm'
						: 'mm',
			second:
				time.second === '2-digit'
					? 'ss'
					: time.second === 'numeric'
						? 's'
						: 'ss',

			ampm: time.ampm ? ' A' : ''
		};

		formatString = `${dynamicFormat.weekday}${dynamicFormat.day}${dateSep}${dynamicFormat.month}${dateSep}${dynamicFormat.year} ${dynamicFormat.hour}${timeSep}${dynamicFormat.minute}${dynamicFormat.ampm}`;
	}

	const m = timeZone
		? moment.tz(dateValue, timeZone).locale(locale)
		: moment.utc(dateValue).locale(locale);

	const displayValue = m.format(formatString);

	// Render
	return <>{displayValue}</>;
}

export function formatDateString({
	dateValue,
	dateFormat,
	date,
	time,
	timeZone = 'UTC',
	locale = 'en',
	ifEmpty = ''
}) {
	if (!dateValue) return ifEmpty || '';
	const m = timeZone
		? moment.tz(dateValue, timeZone).locale(locale)
		: moment.utc(dateValue).locale(locale);

	let formatString = dateFormat || 'DD/MM/YYYY HH:mm';
	const dateSep = date?.dateSeparator || '/';
	const timeSep = time?.timeSeparator || ':';

	if (!dateFormat && (date || time)) {
		const dynamicFormat = {
			day: date?.day === '2-digit' ? 'DD' : date?.day === 'numeric' ? 'D' : date?.day === 'ordinal' ? 'Do' : 'DD',
			month: date?.month === '2-digit' ? 'MM' : date?.month === 'numeric' ? 'M' : date?.month === 'short' ? 'MMM' : date?.month === 'long' ? 'MMMM' : 'MM',
			year: date?.year === '2-digit' ? 'YY' : 'YYYY',
			weekday: date?.weekday === 'short' ? 'ddd, ' : date?.weekday === 'long' ? 'dddd, ' : '',
			hour: time?.hour === '2-digit' ? (time?.ampm ? 'hh' : 'HH') : 'H',
			minute: time?.minute === '2-digit' ? 'mm' : 'm',
			second: time?.second === '2-digit' ? 'ss' : 's',
			ampm: time?.ampm ? ' A' : ''
		};
		formatString = `${dynamicFormat.weekday}${dynamicFormat.day}${dateSep}${dynamicFormat.month}${dateSep}${dynamicFormat.year} ${dynamicFormat.hour}${timeSep}${dynamicFormat.minute}${dynamicFormat.ampm}`;
	}
	return m.format(formatString);
}
